import {
  Badge,
  Box,
  Button,
  Flex,
  type RuleSearchResult,
  RuleSearchResults,
  Text,
  Textarea,
} from '@rocket/ui';
import { useEffect, useRef, useState } from 'react';
import { LuCheck, LuMapPin, LuSend, LuThumbsDown, LuThumbsUp } from 'react-icons/lu';
import { useFtueHost } from '../host';
import type { ChatRuleCreated, ChatRuleSearchResult, ChatSessionContext } from '../onboarding-api';
import { copyRuleToPlaybook, onboardingChat } from '../onboarding-api';
import { RuleExampleCarousel } from './RuleExampleCarousel';

/** Convert ChatRuleSearchResult to RuleSearchResult for RuleSearchCard component */
function convertToRuleSearchResult(chatResult: ChatRuleSearchResult): RuleSearchResult {
  return {
    rule_id: chatResult.ruleId,
    name: chatResult.name,
    description: chatResult.description,
    playbook_id: chatResult.playbookId,
    playbook_name: chatResult.playbookName,
    training_count: chatResult.trainingCount,
    confidence: chatResult.confidence,
    sample_edits: chatResult.sampleEdits || [],
    train_types: [],
    sample_sentences: [],
  };
}

function shouldShowYesNoActions(text: string): boolean {
  const normalized = text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  if (!normalized) {
    return false;
  }

  const explicitYesNo = /(yes\s*\/\s*no|yes or no|y\s*\/\s*n|reply\s+yes|reply\s+no)/i;
  if (explicitYesNo.test(normalized)) {
    return true;
  }

  const asksForAlternativeChoice =
    /\b(quick review|full review|choose|select|pick|which)\b/i.test(normalized) &&
    /\bor\b/i.test(normalized);
  if (asksForAlternativeChoice) {
    return false;
  }

  const hasQuestionMark = normalized.includes('?');
  const binaryIntentPatterns = [
    /shall\s+i/i,
    /should\s+i/i,
    /would\s+you\s+like\s+me\s+to/i,
    /do\s+you\s+want\s+me\s+to/i,
    /want\s+me\s+to/i,
    /ready\s+to\s+proceed/i,
    /may\s+i\s+proceed/i,
    /does\s+this\s+look\s+right/i,
    /does\s+that\s+look\s+right/i,
    /is\s+this\s+(right|ok|okay)/i,
    /is\s+that\s+(right|ok|okay)/i,
    /does\s+this\s+look\s+(ok|okay)/i,
    /does\s+that\s+look\s+(ok|okay)/i,
    /does\s+this\s+seem\s+right/i,
    /does\s+that\s+seem\s+right/i,
    /sound\s+good/i,
  ];
  return hasQuestionMark && binaryIntentPatterns.some((p) => p.test(normalized));
}

/** Renders basic markdown bold (**text**) as <strong> in chat messages. */
function ChatMessageText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <Text key={i} as="span" fontWeight="bold">
              {part.slice(2, -2)}
            </Text>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  rulesCreated?: Array<ChatRuleCreated>;
  searchResults?: Array<ChatRuleSearchResult>;
}

const RULE_FAMILY_LABELS: Record<string, string> = {
  point_edit: 'Point edit',
  fsi: 'Sentence insert',
  fpi: 'Paragraph insert',
  fsd: 'Sentence delete',
  fpd: 'Paragraph delete',
  list_edit: 'List edit',
  comment: 'Comment',
};

function RuleCreatedCard({ rule }: { rule: ChatRuleCreated }) {
  const familyLabel = RULE_FAMILY_LABELS[rule.ruleFamily ?? ''] ?? rule.ruleFamily ?? 'Rule';
  const isTail = rule.alignmentMode === 'TAIL' && !!rule.tailSectionName;
  const before = rule.sampleEdit?.before?.trim() ?? '';
  const after = rule.sampleEdit?.after?.trim() ?? '';

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor="green.muted"
      bg="green.subtle"
      px="3"
      py="2"
      w="100%"
    >
      <Flex.H gap="1" align="center" mb={isTail ? '1' : '0'}>
        <LuCheck size={12} color="var(--chakra-colors-green-fg)" />
        <Text fontSize="xs" fontWeight="600" color="green.fg">
          {rule.name}
        </Text>
        <Badge size="sm" variant="outline" colorPalette="green" ml="1">
          {familyLabel}
        </Badge>
      </Flex.H>
      {rule.description && (
        <Text fontSize="xs" color="fg.muted" mb="1">
          {rule.description}
        </Text>
      )}
      {(before || after) && (
        <Flex.V gap="1" mt="1">
          {before && (
            <Box
              borderWidth="1px"
              borderRadius="sm"
              borderColor="border"
              bg="bg.surface"
              px="2"
              py="1"
            >
              <Text fontSize="2xs" color="fg.subtle" fontWeight="600">
                Before
              </Text>
              <Text fontSize="xs" color="fg">
                {before}
              </Text>
            </Box>
          )}
          <Box
            borderWidth="1px"
            borderRadius="sm"
            borderColor="border"
            bg="bg.surface"
            px="2"
            py="1"
          >
            <Text fontSize="2xs" color="fg.subtle" fontWeight="600">
              After
            </Text>
            <Text fontSize="xs" color="fg">
              {after}
            </Text>
          </Box>
        </Flex.V>
      )}
      {isTail && (
        <Flex.H gap="1" align="center" mt="1">
          <LuMapPin size={10} color="var(--chakra-colors-fg-muted)" />
          <Text fontSize="xs" color="fg.muted">
            Inserts after <strong>{rule.tailSectionName}</strong>
          </Text>
          {rule.contractType && (
            <Text fontSize="xs" color="fg.subtle" ml="1">
              (using {rule.contractType} sections)
            </Text>
          )}
        </Flex.H>
      )}
    </Box>
  );
}

interface SuggestedPrompt {
  label: string;
  example: string;
}

const SUGGESTED_PROMPTS: Array<SuggestedPrompt> = [
  {
    label: 'Search for rules',
    example: 'Find indemnification rules from other playbooks',
  },
  {
    label: 'Add a rule',
    example: 'Add a rule requiring 30 days written notice for termination',
  },
];

interface Props {
  playbookId: string | undefined;
  contractType: string;
  onRulesCreated?: (rules: Array<ChatRuleCreated>) => void;
  /** When true the component fills its parent height (flex column) */
  fillHeight?: boolean;
}

export function ReviewChatBox({ playbookId, contractType, onRulesCreated, fillHeight }: Props) {
  const { bbAxios } = useFtueHost();
  const [messages, setMessages] = useState<Array<ChatMessage>>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sessionContext, setSessionContext] = useState<ChatSessionContext>({});
  const [chatHistory, setChatHistory] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([]);
  const [_addingRuleIds, setAddingRuleIds] = useState<Set<string>>(new Set());
  const [addedRuleIds, setAddedRuleIds] = useState<Set<string>>(new Set());
  const [dismissedCarouselIds, setDismissedCarouselIds] = useState<Set<string>>(new Set());
  const [_dismissedSearchCarouselIds, _setDismissedSearchCarouselIds] = useState<Set<string>>(
    new Set()
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleAddRule = async (result: ChatRuleSearchResult) => {
    if (!playbookId || addedRuleIds.has(result.ruleId)) {
      return;
    }
    setAddingRuleIds((prev) => new Set(prev).add(result.ruleId));
    try {
      await copyRuleToPlaybook(bbAxios, { sourceRuleId: result.ruleId, playbookId });
      setAddedRuleIds((prev) => new Set(prev).add(result.ruleId));
      onRulesCreated?.([{ ruleId: result.ruleId, name: result.name }]);
    } catch {
      // silently ignore — user can retry
    } finally {
      setAddingRuleIds((prev) => {
        const next = new Set(prev);
        next.delete(result.ruleId);
        return next;
      });
    }
  };

  const sendMessage = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || isSending || !playbookId) {
      return;
    }

    const userMsg: ChatMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsSending(true);

    try {
      const result = await onboardingChat(bbAxios, {
        playbookId,
        contractType,
        message: text,
        sessionContext,
        chatHistory,
      });

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: result.reply,
        rulesCreated: result.rulesCreated.length > 0 ? result.rulesCreated : undefined,
        searchResults: result.searchResults?.length > 0 ? result.searchResults : undefined,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setSessionContext(result.sessionContext);
      setChatHistory(result.chatHistory ?? []);

      if (result.rulesCreated.length > 0) {
        onRulesCreated?.(result.rulesCreated);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Something went wrong. Please try again.' },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      borderColor="border"
      p="3"
      display="flex"
      flexDirection="column"
      flex={fillHeight ? '1' : undefined}
      minH={fillHeight ? '0' : '300px'}
      w="100%"
    >
      <Text fontSize="sm" fontWeight="semibold" mb="3">
        Playbook Assistant
      </Text>

      {/* Message history / empty state */}
      <Box ref={scrollRef} flex="1" minH="0" w="100%" overflowY="auto" mb="3">
        {isEmpty ? (
          <Flex.V gap="2" w="100%">
            <Text fontSize="xs" color="fg.muted">
              Try one of these:
            </Text>
            {SUGGESTED_PROMPTS.map((p) => (
              <Button
                key={p.label}
                variant="outline"
                size="sm"
                justifyContent="flex-start"
                h="auto"
                py="2"
                onClick={() => setInput(p.example)}
                disabled={!playbookId}
              >
                <Flex.V gap="0" alignItems="flex-start">
                  <Text fontSize="xs" fontWeight="semibold">
                    {p.label}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" fontWeight="normal">
                    e.g. "{p.example}"
                  </Text>
                </Flex.V>
              </Button>
            ))}
            {!playbookId && (
              <Text fontSize="xs" color="fg.subtle" mt="1">
                Save your rules first to use the assistant.
              </Text>
            )}
          </Flex.V>
        ) : (
          <Box display="flex" flexDirection="column" gap="2" w="100%">
            {messages.map((msg, i) => (
              <Box key={i} w="100%">
                {/* Chat bubble */}
                <Box
                  bg={msg.role === 'user' ? 'blue.subtle' : 'bg.surface'}
                  borderRadius="md"
                  px="3"
                  py="2"
                  maxW="90%"
                  css={{ marginLeft: msg.role === 'user' ? 'auto' : undefined }}
                >
                  <Text fontSize="xs" whiteSpace="pre-wrap">
                    <ChatMessageText text={msg.content} />
                  </Text>
                </Box>

                {/* Yes / No quick-reply buttons */}
                {msg.role === 'assistant' &&
                  i === messages.length - 1 &&
                  !isSending &&
                  !messages.slice(i + 1).some((m) => m.role === 'user') &&
                  shouldShowYesNoActions(msg.content) && (
                    <Flex.H mt="2" gap="2">
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="green"
                        disabled={isSending}
                        onClick={() => void sendMessage('yes')}
                      >
                        <LuThumbsUp size={12} />
                        Looks good
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        disabled={isSending}
                        onClick={() => void sendMessage('no')}
                      >
                        <LuThumbsDown size={12} />
                        No
                      </Button>
                    </Flex.H>
                  )}

                {/* Rule-created cards */}
                {msg.rulesCreated && msg.rulesCreated.length > 0 && (
                  <Box mt="1" display="flex" flexDirection="column" gap="1">
                    {msg.rulesCreated.map((r) => (
                      <Box key={r.ruleId}>
                        <RuleCreatedCard rule={r} />
                        {!dismissedCarouselIds.has(r.ruleId) && (
                          <RuleExampleCarousel
                            ruleId={r.ruleId}
                            onDismiss={() =>
                              setDismissedCarouselIds((prev) => new Set(prev).add(r.ruleId))
                            }
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Search result cards - now using RuleSearchCard with inline sample edits */}
                {msg.searchResults && msg.searchResults.length > 0 && (
                  <Box mt="2">
                    {/* Show section fallback message if applicable */}
                    {msg.searchResults.some((r) => r.sectionFallback) && (
                      <Box mb="2" p="2" bg="blue.subtle" borderRadius="sm">
                        <Text fontSize="xs" color="blue.fg">
                          No exact matches found. Here are rules from the{' '}
                          <strong>
                            {msg.searchResults.find((r) => r.sectionFallback)?.sectionFallback}
                          </strong>{' '}
                          section:
                        </Text>
                      </Box>
                    )}
                    <RuleSearchResults
                      results={msg.searchResults.map(convertToRuleSearchResult)}
                      onSelect={(rule) => {
                        // Convert back to ChatRuleSearchResult to call handleAddRule
                        const chatResult = msg.searchResults?.find(
                          (r) => r.ruleId === rule.rule_id
                        );
                        if (chatResult) {
                          void handleAddRule(chatResult);
                        }
                      }}
                      disabled={isSending}
                    />
                    {/* Show "Added" status badges after the cards */}
                    {msg.searchResults.some((r) => addedRuleIds.has(r.ruleId)) && (
                      <Box mt="2">
                        {msg.searchResults
                          .filter((r) => addedRuleIds.has(r.ruleId))
                          .map((result) => (
                            <Flex.H key={result.ruleId} align="center" gap="1" mb="1">
                              <LuCheck size={12} color="var(--colors-green-fg)" />
                              <Text fontSize="xs" color="green.fg">
                                {result.name} added to playbook
                              </Text>
                            </Flex.H>
                          ))}
                      </Box>
                    )}
                    {/* "Get more results" button if this was a search result message */}
                    {i === messages.length - 1 && msg.searchResults.length > 0 && !isSending && (
                      <Button
                        size="xs"
                        variant="outline"
                        mt="2"
                        onClick={() => void sendMessage('Show me 5 more results')}
                      >
                        Get 5 more results
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            ))}
            {isSending && (
              <Box bg="bg.surface" borderRadius="md" px="3" py="2" maxW="60%">
                <Text fontSize="xs" color="fg.muted">
                  Thinking…
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Input */}
      <Flex.H gap="2" align="flex-end">
        <Box flex="1">
          <Textarea
            placeholder="Ask about rules or describe a new one…"
            value={input}
            onChange={(e) => setInput((e.target as HTMLTextAreaElement).value)}
            onKeyDown={handleKeyDown}
            disabled={isSending || !playbookId}
            rows={2}
          />
        </Box>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={() => void sendMessage()}
          disabled={!input.trim() || isSending || !playbookId}
          loading={isSending}
          aria-label="Send"
        >
          <LuSend size={14} />
        </Button>
      </Flex.H>
    </Box>
  );
}
